import React from 'react';
import useDoctorScheduleStore from './useDoctorScheduleStore';
import ScheduleBadge from './ScheduleBadge';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = { monday: 'MON', tuesday: 'TUE', wednesday: 'WED', thursday: 'THU', friday: 'FRI', saturday: 'SAT', sunday: 'SUN' };

function DoctorCell({ doctor, translations }) {
  const isSync = doctor.source_label === 'synced';
  const badgeClass = isSync
    ? 'doctor-schedule-mgmt__source-badge--synced'
    : 'doctor-schedule-mgmt__source-badge--manual';
  const badgeText = isSync ? translations.badges.synced : translations.badges.manual;

  return (
    <a href={doctor.profile_path} className="doctor-schedule-mgmt__doctor-link">
      <div className="doctor-schedule-mgmt__doctor-cell">
        <div className="doctor-schedule-mgmt__doctor-avatar">
          {doctor.avatar_url ? (
            <img src={doctor.avatar_url} alt={doctor.name} className="doctor-schedule-mgmt__avatar-img" />
          ) : (
            <div className="doctor-schedule-mgmt__avatar-placeholder">
              <i className="zmdi zmdi-account" />
            </div>
          )}
        </div>
        <div className="doctor-schedule-mgmt__doctor-info">
          <div className="doctor-schedule-mgmt__doctor-name-row">
            <span className="doctor-schedule-mgmt__doctor-name" title={doctor.name}>
              {doctor.name}
            </span>
            <span className={`doctor-schedule-mgmt__source-badge ${badgeClass}`}>
              {badgeText}
            </span>
          </div>
          <div className="doctor-schedule-mgmt__doctor-specialty">
            {doctor.speciality}
          </div>
          {doctor.hospital_branch && (
            <div className="doctor-schedule-mgmt__doctor-branch">
              <i className="zmdi zmdi-hospital mr-1" />
              {doctor.hospital_branch}
            </div>
          )}
        </div>
      </div>
    </a>
  );
}

function DayCell({ slots, translations }) {
  if (!slots || slots.length === 0) {
    return <span className="doctor-schedule-mgmt__empty-cell">&mdash;</span>;
  }

  return (
    <div className="doctor-schedule-mgmt__day-cell">
      {slots.map((slot, index) => (
        <ScheduleBadge
          key={`${slot.from_time}-${slot.type}-${index}`}
          fromTime={slot.from_time}
          toTime={slot.to_time}
          type={slot.type}
          label={slot.label}
          source={slot.source}
          scheduleLabels={translations.schedule_types}
        />
      ))}
    </div>
  );
}

function WeeklyGridView({ translations }) {
  const doctors = useDoctorScheduleStore((s) => s.doctors);
  const loading = useDoctorScheduleStore((s) => s.loading);
  const t = translations.grid;

  if (loading) {
    const skeletonRows = Array.from({ length: 6 });
    return (
      <div className="card doctor-schedule-mgmt__grid-card">
        <div className="table-responsive">
          <table className="table table-bordered doctor-schedule-mgmt__grid-table mb-0">
            <thead>
              <tr>
                <th className="doctor-schedule-mgmt__th-doctor">
                  <i className="zmdi zmdi-sort-amount-asc mr-1" />
                  {t.doctor}
                </th>
                {DAYS.map((day) => (
                  <th key={day} className="doctor-schedule-mgmt__th-day text-center">
                    {t.days[DAY_LABELS[day].toLowerCase()] || DAY_LABELS[day]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {skeletonRows.map((_, i) => (
                <tr key={i}>
                  <td className="doctor-schedule-mgmt__td-doctor">
                    <div className="doctor-schedule-mgmt__skeleton-cell d-flex align-items-center">
                      <div className="doctor-schedule-mgmt__skeleton doctor-schedule-mgmt__skeleton--circle" />
                      <div className="ml-2 flex-grow-1">
                        <div className="doctor-schedule-mgmt__skeleton doctor-schedule-mgmt__skeleton--text-lg mb-1" />
                        <div className="doctor-schedule-mgmt__skeleton doctor-schedule-mgmt__skeleton--text-sm mb-1" />
                        <div className="doctor-schedule-mgmt__skeleton doctor-schedule-mgmt__skeleton--text-xs" />
                      </div>
                    </div>
                  </td>
                  {DAYS.map((day) => (
                    <td key={day} className="text-center align-middle">
                      <div className="doctor-schedule-mgmt__skeleton doctor-schedule-mgmt__skeleton--badge mx-auto" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (doctors.length === 0) {
    return (
      <div className="card">
        <div className="card-body text-center py-5 text-muted">
          <i className="zmdi zmdi-calendar-note zmdi-hc-3x mb-3 d-block" />
          {translations.empty_state.no_results}
        </div>
      </div>
    );
  }

  return (
    <div className="card doctor-schedule-mgmt__grid-card">
      <div className="table-responsive">
        <table className="table table-bordered doctor-schedule-mgmt__grid-table mb-0">
          <thead>
            <tr>
              <th className="doctor-schedule-mgmt__th-doctor">
                <i className="zmdi zmdi-account mr-1" />
                {t.doctor}
              </th>
              {DAYS.map((day) => (
                <th key={day} className="doctor-schedule-mgmt__th-day text-center">
                  {t.days[DAY_LABELS[day].toLowerCase()] || DAY_LABELS[day]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {doctors.map((doctor) => (
              <tr key={doctor.id} className="doctor-schedule-mgmt__row">
                <td className="doctor-schedule-mgmt__td-doctor">
                  <DoctorCell doctor={doctor} translations={translations} />
                </td>
                {DAYS.map((day) => (
                  <td key={day} className="doctor-schedule-mgmt__td-day text-center align-middle">
                    <DayCell slots={doctor.schedules[day]} translations={translations} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default WeeklyGridView;
